import { defineStore } from 'pinia';
import useAcvDatadog from '@/composables/useAcvDatadog';
import { optimizeJobPhotoUrls } from '@/composables/useOptimizedPhotoUrl';

const { addRumError } = useAcvDatadog();

export const useStagedJobsStore = defineStore({
  id: 'stagedJobsStore',
  state: () => {
    return {
      jobs: [],
      pagination: {
        first: true,
        last: false,
        number: 0,
        numberOfElements: 17,
        size: 50,
        totalElements: 0,
        totalPages: 1,
      },
      currentPage: 0,
      rowsPerPage: '50',
      successfullyStaged: null,
      pendingStaged: null,
      bookedStatus: null,
      failedToStage: [],
      currentPageIndex: 0,
    };
  },
  actions: {
    async getStagedJobs() {
      const requestParams = {
        page: this.currentPageIndex,
        size: this.rowsPerPage,
        sort: `asc`,
      };
      const { $api } = useNuxtApp();
      let content = null;
      let error = null;
      try {
        content = await $api(`api/transport-requests/staged`, {
          method: 'GET',
          params: requestParams,
        });
      } catch (err) {
        error = err;
      }

      if (error) {
        if (error.statusCode === 403) {
          addRumError('403 error from useStagedJob - getStagedJobs', {
            errorMessage: error.message,
            errorStatus: error.statusCode,
            requestParams: requestParams,
          });
        }

        return;
      }
      this.jobs = optimizeJobPhotoUrls(content.data.content, '200x200');
      this.pagination = content.data.pagination;
    },
    async bookStagedJob(
      body,
      { orderId, carModel, isInstantBook, bundleId, isRequireVerification },
    ) {
      const { $api } = useNuxtApp();
      let error = null;
      try {
        await $api(`/api/transport-requests/bookStagedJob`, {
          method: 'POST',
          body,
        });
      } catch (err) {
        error = err;
      }

      await this.getStagedJobs();
      const details = {
        orderId,
        bundleId,
        carModel,
        id: body.transportRequestId,
        isRequireVerification,
      };

      if (error) {
        this.bookedStatus = {
          ...details,
          bookedType: 'error',
        };
        addRumError('Error booking staged job', {
          error: error,
          bookedStatus: this.bookedStatus,
          bundle_id: bundleId ?? null,
          order_id: orderId ?? null,
        });
      } else {
        this.successfullyStaged = isInstantBook ? details : null;
        this.pendingStaged = isInstantBook ? null : details;
        this.bookedStatus = {
          ...details,
          bookedType: isInstantBook ? 'instant' : 'pending',
        };
      }
      this.failedToStage = [];
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
    async unstageJob(requestId) {
      const { $api } = useNuxtApp();
      await $api(`/api/transport-requests/unstage/${requestId}`, {
        method: 'GET',
      });

      await this.getStagedJobs();
    },
    clearNotificationsLists() {
      this.successfullyStaged = null;
      this.pendingStaged = null;
      this.bookedStatus = null;
      this.failedToStage = [];
    },
    setFailedToStage(requestId) {
      this.failedToStage = requestId;
    },
  },
});
